import type { FC } from 'react';

import { georgeStats, trackEvent2 } from '@shared/statistics';
import type { AliasChangeDropdownProps } from '@shared/products-customization';
import { AliasChangeDropdown as SharedAliasChangeDropdown } from '@shared/products-customization';
import { useActiveCustomizationFeature } from '@shared/products-core';
import { parseRegExpFromString } from '@luna/utils';
import { getFeatureConfig } from '@luna/feature-config';
import { useNotifications } from '@shared/snackbar';
import { t } from '@luna/i18n';

type Props = Omit<
    AliasChangeDropdownProps,
    'onClose' | 'validationConfig' | 'maxInputLength' | 'isOpen' | 'onOpen'
> & {
    aliasChangeContext: string;
    productType: string;
    placeholder?: string;
} & Parameters<typeof trackEvent2<'productCustomisationChanges', 'productNameChangeStarted'>>[2];

export const AliasChangeDropdown: FC<Props> = ({
    toggle,
    alias = '',
    onAliasSave,
    onAliasChangeSuccess,
    onAliasChangeError,
    productType,
    aliasChangeContext,
    hasAlias,
    placeholder,
    trigger,
}) => {
    const { resetActiveCustomization, isAliasChangeCustomizationActive } =
        useActiveCustomizationFeature();
    const { displaySuccess } = useNotifications('product-settings-details-dashboard-appearance');

    const closeAliasDropdown = () => {
        isAliasChangeCustomizationActive && resetActiveCustomization();
    };
    const handleAliasChangeSuccess = () => {
        georgeStats.trackEvent(aliasChangeContext, 'name_change', { productType });
        trackEvent2('productCustomisationChanges', 'productCustomised', {
            productType,
            customisationChanges: ['name'],
        });
        displaySuccess(t('products.settings.details.aliasChanged'));
        onAliasChangeSuccess?.();
        closeAliasDropdown();
    };

    const handleAliasChangeStart = () => {
        trackEvent2('productCustomisationChanges', 'productNameChangeStarted', {
            trigger,
        });
    };

    const { accountNameLength, accountNameMatchString, accountNameMinimumLength } =
        getFeatureConfig(({ accounts }) => accounts);

    const validationConfig = {
        maxLength: accountNameLength,
        minLength: accountNameMinimumLength,
        allowedCharacters: parseRegExpFromString(accountNameMatchString),
    };

    return (
        <SharedAliasChangeDropdown
            toggle={toggle}
            onAliasSave={onAliasSave}
            validationConfig={validationConfig}
            onClose={closeAliasDropdown}
            alias={alias}
            onAliasChangeError={onAliasChangeError}
            hasAlias={hasAlias}
            isOpen={isAliasChangeCustomizationActive}
            onAliasChangeSuccess={handleAliasChangeSuccess}
            placeholder={placeholder}
            onOpen={handleAliasChangeStart}
            maxInputLength={35}
        />
    );
};
